"use client"

import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react"
import { Dialog, DialogContent } from "@/components/ui/overlay/dialog"

const shellClass =
  "flex h-dvh max-h-dvh flex-col gap-0 overflow-hidden bg-white px-4 pt-[max(1rem,env(safe-area-inset-top,0px))] pb-[max(1rem,env(safe-area-inset-bottom,0px))] dark:bg-card md:h-auto md:max-h-[min(100dvh-2rem,900px)] md:w-[540px] md:max-w-[540px] md:rounded-2xl md:p-7 [&>button.absolute]:hidden"

/** Country search and other popovers render outside the card, but they are still part of this step. */
function targetInPopover(target: EventTarget | null) {
  return target instanceof Element && Boolean(target.closest("[data-radix-popper-content-wrapper]"))
}

const EscapeContext = createContext<(handler: (() => void) | null) => void>(() => {})
const PromptContext = createContext<(node: ReactNode | null) => void>(() => {})

/** Renders `node` over the onboarding card while `active`. */
export function useOnboardingPrompt(active: boolean, node: ReactNode) {
  const setPrompt = useContext(PromptContext)
  const nodeRef = useRef(node)
  nodeRef.current = node
  useEffect(() => {
    if (!active) return
    setPrompt(nodeRef.current)
    return () => setPrompt(null)
  }, [active, setPrompt])
}

/** While `active`, Escape runs `handler` instead of asking to leave. */
export function useOnboardingEscape(active: boolean, handler: () => void) {
  const setHandler = useContext(EscapeContext)
  const handlerRef = useRef(handler)
  handlerRef.current = handler
  useEffect(() => {
    if (!active) return
    setHandler(() => handlerRef.current())
    return () => setHandler(null)
  }, [active, setHandler])
}

export function OnboardingDialog({
  open,
  onClose,
  describedBy,
  leaveTitle = "Leave onboarding?",
  leaveBody = "Your progress so far is saved. You can continue later.",
  children,
}: {
  open: boolean
  onClose: () => void
  describedBy: string
  leaveTitle?: string
  leaveBody?: string
  children: (requestLeave: () => void) => ReactNode
}) {
  const [confirmLeave, setConfirmLeave] = useState(false)
  const [prompt, setPrompt] = useState<ReactNode>(null)
  const escapeRef = useRef<(() => void) | null>(null)
  const setEscapeHandler = useCallback((handler: (() => void) | null) => {
    escapeRef.current = handler
  }, [])
  const setPromptNode = useCallback((node: ReactNode | null) => {
    setPrompt(node)
  }, [])

  useEffect(() => {
    if (!open) setConfirmLeave(false)
  }, [open])

  function requestLeave() {
    setConfirmLeave(true)
  }

  return (
    <EscapeContext.Provider value={setEscapeHandler}>
    <PromptContext.Provider value={setPromptNode}>
      <Dialog open={open} onOpenChange={(next) => { if (!next) requestLeave() }}>
        <DialogContent
          aria-describedby={describedBy}
          className={shellClass}
          onPointerDownOutside={(event) => {
            event.preventDefault()
            if (targetInPopover(event.target)) return
            requestLeave()
          }}
          onInteractOutside={(event) => {
            event.preventDefault()
            if (targetInPopover(event.target)) return
            requestLeave()
          }}
          onEscapeKeyDown={(event) => {
            event.preventDefault()
            if (confirmLeave) {
              setConfirmLeave(false)
              return
            }
            if (escapeRef.current) {
              escapeRef.current()
              return
            }
            requestLeave()
          }}
        >
          {children(requestLeave)}
          {prompt}
          {confirmLeave ? (
            <OnboardingPrompt
              className="absolute inset-0 z-40"
              titleId="leave-onboarding-title"
              bodyId="leave-onboarding-body"
              title={leaveTitle}
              body={leaveBody}
              icon={<LeaveIcon />}
              secondaryLabel="Leave"
              primaryLabel="Keep going"
              onSecondary={() => {
                setConfirmLeave(false)
                onClose()
              }}
              onPrimary={() => setConfirmLeave(false)}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </PromptContext.Provider>
    </EscapeContext.Provider>
  )
}

export function OnboardingPrompt({
  className,
  titleId,
  bodyId,
  title,
  body,
  icon,
  secondaryLabel,
  primaryLabel,
  onSecondary,
  onPrimary,
}: {
  className: string
  titleId: string
  bodyId: string
  title: string
  body: string
  icon: ReactNode
  secondaryLabel: string
  primaryLabel: string
  onSecondary: () => void
  onPrimary: () => void
}) {
  return (
    <div className={`flex items-end bg-[rgba(17,24,39,0.45)] p-7 ${className}`}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        className="flex w-full flex-col gap-5 rounded-[14px] bg-white p-6 text-[#1F2328] shadow-[0_12px_32px_rgba(17,24,39,0.18)] dark:bg-card dark:text-foreground"
      >
        <div className="flex items-start gap-3.5">
          {icon}
          <div className="flex flex-col gap-1.5">
            <h2 id={titleId} className="m-0 text-[18px] font-bold tracking-[-0.01em]">{title}</h2>
            <p id={bodyId} className="m-0 text-[15px] leading-normal text-[#4B5563] dark:text-muted-foreground">{body}</p>
          </div>
        </div>
        <div className="flex gap-2.5">
          <button
            type="button"
            onClick={onSecondary}
            className="min-h-11 flex-1 rounded-[10px] border border-[#D1D5DB] bg-white px-[18px] text-[15px] font-medium text-[#1F2328] dark:border-border dark:bg-card dark:text-foreground"
          >
            {secondaryLabel}
          </button>
          <button
            type="button"
            autoFocus
            onClick={onPrimary}
            className="min-h-11 flex-1 rounded-[10px] border-0 bg-[#3B5FC4] px-[18px] text-[15px] font-semibold text-white hover:bg-[#2C4A9E]"
          >
            {primaryLabel}
          </button>
        </div>
      </div>
    </div>
  )
}

function LeaveIcon() {
  return (
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#EEF2FC] text-[#3B5FC4] dark:bg-primary/15" aria-hidden>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4M10 17l5-5-5-5M15 12H3" />
      </svg>
    </span>
  )
}
